import React, { useState, useEffect, useRef } from 'react';
import './ModalPermissoes.css';
import CryptoJS from 'crypto-js';
import ModalSenhaMaster from './ModalSenhaMaster';
import { API_URLS } from '../config/api';

const API_URL = API_URLS.PERMISSOES;

const PAPEIS_DISPONIVEIS = [
  { valor: 'adm-full', label: 'DEV/Admin (Acesso Total)', descricao: 'Acesso completo a todos os módulos' },
  { valor: 'licitacao-full', label: 'Licitação (Full)', descricao: 'Acesso completo ao módulo Licitação' },
  { valor: 'compras-full', label: 'Compras (Full)', descricao: 'Acesso completo ao módulo Compras' },
  { valor: 'operacional-full', label: 'Operacional (Full)', descricao: 'Acesso completo ao módulo Operacional' },
  { valor: 'treinamentos-full', label: 'Treinamentos (Full)', descricao: 'Acesso completo ao módulo Treinamentos' },
  { valor: 'controladoria-full', label: 'Controladoria (Full)', descricao: 'Acesso à Dashboard, Solicitar Compras, ERP, Sistemas, Power BI, Contratos e SEI' }
];

function ModalPermissoes({ isOpen, onClose }) {
  const modalBodyRef = useRef(null);
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('nome');
  const [loading, setLoading] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const [formData, setFormData] = useState({
    cpf: '',
    nome: '',
    cargo: '',
    setor: '',
    papel: 'adm-full'
  });
  
  // Estados para senha master
  const [modalSenhaMasterAberto, setModalSenhaMasterAberto] = useState(false);
  const [senhaMasterAtiva, setSenhaMasterAtiva] = useState(null);
  const [senhasDescriptografadas, setSenhasDescriptografadas] = useState({});

  useEffect(() => {
    if (isOpen) {
      carregarUsuarios();
    }
  }, [isOpen]);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL, {
        headers: {
          'Accept': 'application/json; charset=utf-8'
        }
      });
      const data = await response.json();
      if (data.success) {
        console.log('📋 [FRONTEND] Usuários carregados:', data.data.length);
        // Debug UTF-8: Mostrar primeiro usuário completo
        if (data.data.length > 0) {
          console.log('🔍 [UTF-8 DEBUG] Primeiro usuário:', JSON.stringify(data.data[0], null, 2));
          console.log('🔍 [UTF-8 DEBUG] Nome:', data.data[0].nome);
          console.log('🔍 [UTF-8 DEBUG] Cargo:', data.data[0].cargo);
          console.log('🔍 [UTF-8 DEBUG] Setor:', data.data[0].setor);
        }
        setUsuarios(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  // Função chamada quando senha master for validada
  const handleSenhaMasterCorreta = (senhaMaster) => {
    console.log('✅ [FRONTEND] Senha master validada! Descriptografando senhas...');
    
    // Descriptografar todas as senhas
    const senhasDecrypt = {};
    let sucessos = 0;
    let erros = 0;
    
    usuarios.forEach(usuario => {
      if (usuario.senha_crypto) {
        try {
          const bytes = CryptoJS.AES.decrypt(usuario.senha_crypto, senhaMaster);
          const senhaPlainText = bytes.toString(CryptoJS.enc.Utf8);
          
          if (senhaPlainText) {
            senhasDecrypt[usuario.cpf] = senhaPlainText;
            sucessos++;
            console.log(`   ✅ Descriptografado: ${usuario.nome} - ${senhaPlainText}`);
          } else {
            console.log(`   ❌ Falha: ${usuario.nome} - senha vazia`);
            erros++;
          }
        } catch (error) {
          console.error(`   ❌ Erro ao descriptografar ${usuario.nome}:`, error);
          erros++;
        }
      }
    });
    
    console.log(`📊 [FRONTEND] Descriptografia concluída: ${sucessos} sucessos, ${erros} erros`);
    console.log('🔍 [DEBUG] senhasDecrypt objeto:', senhasDecrypt);
    console.log('🔍 [DEBUG] Total de senhas no objeto:', Object.keys(senhasDecrypt).length);
    
    // IMPORTANTE: Primeiro atualizar as senhas, depois ativar a master
    setSenhasDescriptografadas(senhasDecrypt);
    setSenhaMasterAtiva(senhaMaster);
  };

  // Função para bloquear senhas novamente
  const handleBloquearSenhas = () => {
    console.log('🔒 [FRONTEND] Bloqueando senhas...');
    setSenhaMasterAtiva(null);
    setSenhasDescriptografadas({});
  };

  const handleExportar = () => {
    const csv = [
      ['CPF', 'Nome', 'Cargo', 'Setor', 'Login', 'Senha', 'Papel', 'Status'],
      ...usuariosFiltrados.map(u => [
        u.cpf,
        u.nome,
        u.cargo,
        u.setor,
        u.login || 'N/A',
        u.senha || 'N/A',
        u.permissoes?.papel || 'N/A',
        u.ativo ? 'Ativo' : 'Inativo'
      ])
    ].map(row => row.join(',')).join('\n');

    // Adicionar BOM UTF-8 para forçar encoding correto
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios_permissoes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleNovo = () => {
    setEditandoUsuario(null);
    setFormData({
      cpf: '',
      nome: '',
      cargo: '',
      setor: '',
      login: '',
      senha: '',
      papel: 'adm-full'
    });
  };

  const handleEditar = (usuario) => {
    setEditandoUsuario(usuario.cpf);
    setFormData({
      cpf: usuario.cpf,
      nome: usuario.nome,
      cargo: usuario.cargo,
      setor: usuario.setor,
      login: usuario.login || '',
      senha: usuario.senha || '',
      papel: usuario.permissoes?.papel || 'adm-full'
    });
    
    // Rolar para o topo do modal automaticamente
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSalvar = async () => {
    if (!formData.cpf || !formData.nome || !formData.cargo || !formData.setor) {
      alert('Preencha todos os campos obrigatórios!');
      return;
    }

    try {
      setLoading(true);

      const permissoes = {
        papel: formData.papel,
        modulos: obterModulosPorPapel(formData.papel)
      };

      // Gerar login e senha automaticamente com base no CPF
      const loginGerado = formData.login || `${formData.cpf}@hagg`;
      const senhaGerada = formData.senha || `${formData.cpf}@${new Date().getFullYear()}*`;

      const body = {
        cpf: formData.cpf,
        nome: formData.nome,
        cargo: formData.cargo,
        setor: formData.setor,
        login: loginGerado,
        senha: senhaGerada,
        permissoes
      };

      const url = editandoUsuario 
        ? `${API_URL}/${editandoUsuario}`
        : API_URL;

      const method = editandoUsuario ? 'PUT' : 'POST';

      console.log('📤 Enviando para backend:', body);

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      console.log('📥 Resposta do backend:', data);

      if (data.success) {
        alert(editandoUsuario ? 'Usuário atualizado!' : 'Usuário criado!');
        carregarUsuarios();
        handleNovo();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = async (cpf, nome) => {
    if (!window.confirm(`Tem certeza que deseja deletar ${nome}?`)) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/${cpf}`, { method: 'DELETE' });
      const data = await response.json();

      if (data.success) {
        alert('Usuário deletado!');
        carregarUsuarios();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
      alert('Erro ao deletar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAtivo = async (cpf) => {
    try {
      const response = await fetch(`${API_URL}/${cpf}/toggle`, { method: 'PATCH' });
      const data = await response.json();

      if (data.success) {
        carregarUsuarios();
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const obterModulosPorPapel = (papel) => {
    const mapaPapeis = {
      'adm-full': ['*'], // Acesso total
      
      'licitacao-full': [
        'DASHBOARD_GERAL',
        'POWERBI',
        'BASE_CONTRATOS_EDIT', // Pode editar/excluir contratos
        'DASHBOARD_LICITACAO',
        'COTACOES_IMPUGNACOES',
        'CONTROLE_ANUAL_CONTRATOS',
        'CONTROLE_ANUAL_CONTRATOS_VIEW',
        'GRADE_LICITACAO',
        'GRADE_LICITACAO_VIEW',
        'CONTROLE_CONTRATOS_VIGENTES',
        'CONTROLE_CONTRATOS_VIGENTES_VIEW',
        'CRIAR_CONTRATOS'
      ],
      
      'compras-full': [
        'DASHBOARD_GERAL',
        'POWERBI',
        'BASE_CONTRATOS_VIEW', // Apenas visualização
        'DASHBOARD_COMPRAS',
        'MANUTENCAO',
        'COMPRAS_LICITACAO',
        'ESTOQUE',
        'PEDIDOS_EXTERNOS'
      ],

      'operacional-full': [
        'DASHBOARD_GERAL',
        'DASHBOARD_OPERACIONAL',
        'SOLICITACOES_VAGAS_OPERACIONAL'
      ],

      'treinamentos-full': [
        'DASHBOARD_GERAL',
        'DASHBOARD_TREINAMENTOS',
        'SOLICITACOES_VAGAS_TREINAMENTOS'
      ],

      'controladoria-full': [
        'DASHBOARD_GERAL',
        'SOLICITAR_COMPRA',
        'SOLICITAR_ERP',
        'SOLICITAR_SETOR_SISTEMAS',
        'POWERBI',
        'BASE_CONTRATOS_VIEW',
        'SEI_INTERNO'
      ]
    };

    return mapaPapeis[papel] || ['DASHBOARD_GERAL'];
  };

  const usuariosFiltrados = usuarios.filter(u => {
    if (!filtro) return true;
    const valorFiltro = filtro.toLowerCase();
    
    switch (filtroTipo) {
      case 'nome': return u.nome?.toLowerCase().includes(valorFiltro);
      case 'cargo': return u.cargo?.toLowerCase().includes(valorFiltro);
      case 'setor': return u.setor?.toLowerCase().includes(valorFiltro);
      case 'cpf': return u.cpf?.includes(filtro);
      case 'permissao': return u.permissoes?.papel?.toLowerCase().includes(valorFiltro);
      default: return true;
    }
  });

  if (!isOpen) return null;

  return (
    <div className="modal-permissoes-overlay">
      <div className="modal-permissoes-content">
        <div className="modal-permissoes-header">
          <button className="btn-voltar" onClick={onClose}>
            <i className="fas fa-arrow-left"></i> Voltar
          </button>
          <h2><i className="fas fa-user-shield"></i> Gerenciar Permissões</h2>
          <button className="btn-exportar" onClick={handleExportar}>
            <i className="fas fa-download"></i> Exportar
          </button>
        </div>

        <div className="modal-permissoes-body" ref={modalBodyRef}>
          {/* FORMULÁRIO */}
          <div className="form-usuario">
            <h3>{editandoUsuario ? '✏️ Editar Usuário' : '➕ Criar Novo Usuário'}</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>CPF *</label>
                <input 
                  type="text" 
                  value={formData.cpf}
                  onChange={(e) => setFormData({...formData, cpf: e.target.value.replace(/\D/g, '')})}
                  maxLength="11"
                  disabled={!!editandoUsuario}
                  placeholder="Somente números"
                />
              </div>

              <div className="form-group">
                <label>Nome Completo *</label>
                <input 
                  type="text" 
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Nome do funcionário"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cargo *</label>
                <input 
                  type="text" 
                  value={formData.cargo}
                  onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                  placeholder="Ex: Gerente De Compras"
                />
              </div>

              <div className="form-group">
                <label>Setor *</label>
                <select 
                  value={formData.setor}
                  onChange={(e) => setFormData({...formData, setor: e.target.value})}
                >
                  <option value="">Selecione...</option>
                  <option value="Compras">Compras</option>
                  <option value="Licitação">Licitação</option>
                  <option value="Financeiro">Financeiro</option>
                  <option value="Operacional">Operacional</option>
                  <option value="Treinamentos">Treinamentos</option>
                  <option value="TI">TI</option>
                  <option value="RH">RH</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Login (gerado automaticamente)</label>
                <input 
                  type="text" 
                  value={formData.login || `${formData.cpf}@hagg`}
                  onChange={(e) => setFormData({...formData, login: e.target.value})}
                  placeholder="cpf@hagg"
                  readOnly={!editandoUsuario}
                />
              </div>

              <div className="form-group">
                <label>Senha (gerada automaticamente)</label>
                <input 
                  type="text" 
                  value={formData.senha || ''}
                  onChange={(e) => setFormData({...formData, senha: e.target.value})}
                  placeholder={`${formData.cpf}@${new Date().getFullYear()}*`}
                  readOnly={!editandoUsuario}
                />
                {!formData.senha && (
                  <small style={{color: '#888', fontSize: '0.85em', marginTop: '4px', display: 'block'}}>
                    ℹ️ Se vazio, será gerada como: {formData.cpf}@{new Date().getFullYear()}*
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Papel/Permissão *</label>
                <select 
                  value={formData.papel}
                  onChange={(e) => setFormData({...formData, papel: e.target.value})}
                >
                  {PAPEIS_DISPONIVEIS.map(p => (
                    <option key={p.valor} value={p.valor} title={p.descricao}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-salvar" onClick={handleSalvar} disabled={loading}>
                <i className="fas fa-save"></i> {editandoUsuario ? 'Atualizar' : 'Criar'}
              </button>
              {editandoUsuario && (
                <button className="btn-cancelar" onClick={handleNovo}>
                  <i className="fas fa-times"></i> Cancelar
                </button>
              )}
            </div>
          </div>

          {/* FILTROS */}
          <div className="filtros-container">
            <h3>🔍 Filtrar Usuários</h3>
            <div className="filtros-row">
              <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="nome">Nome</option>
                <option value="cargo">Cargo</option>
                <option value="setor">Setor</option>
                <option value="cpf">CPF</option>
                <option value="permissao">Permissão</option>
              </select>
              <input 
                type="text" 
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder={`Buscar por ${filtroTipo}...`}
              />
            </div>
          </div>

          {/* TABELA */}
          <div className="tabela-container">
            <h3>👥 Usuários Cadastrados ({usuariosFiltrados.length})</h3>
            
            {loading && <p className="loading">Carregando...</p>}
            
            <div className="tabela-scroll">
              <table className="tabela-usuarios">
                <thead>
                  <tr>
                    <th>CPF</th>
                    <th>Nome</th>
                    <th>Cargo</th>
                    <th>Setor</th>
                    <th>Login</th>
                    <th>
                      Senha
                      {!senhaMasterAtiva ? (
                        <button 
                          className="btn-liberar-senhas"
                          onClick={() => setModalSenhaMasterAberto(true)}
                          title="Liberar visualização de senhas"
                        >
                          🔓 Liberar
                        </button>
                      ) : (
                        <button 
                          className="btn-bloquear-senhas"
                          onClick={handleBloquearSenhas}
                          title="Bloquear visualização de senhas"
                        >
                          🔒 Bloquear
                        </button>
                      )}
                    </th>
                    <th>Papel/Permissão</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosFiltrados.map(usuario => (
                    <tr key={usuario.cpf} className={!usuario.ativo ? 'inativo' : ''}>
                      <td>{usuario.cpf}</td>
                      <td>{usuario.nome}</td>
                      <td>{usuario.cargo}</td>
                      <td>
                        <span className={`badge-setor badge-${usuario.setor.toLowerCase()}`}>
                          {usuario.setor}
                        </span>
                      </td>
                      <td><code>{usuario.login || 'N/A'}</code></td>
                      <td>
                        {senhaMasterAtiva && senhasDescriptografadas[usuario.cpf] ? (
                          <code className="senha-visivel">
                            🔓 {senhasDescriptografadas[usuario.cpf]}
                          </code>
                        ) : (
                          <code className="senha-oculta" title="Clique em 'Liberar' no cabeçalho para visualizar">
                            🔒 ************
                          </code>
                        )}
                      </td>
                      <td>
                        <span className={`badge-papel badge-${usuario.permissoes?.papel?.toLowerCase()}`}>
                          {usuario.permissoes?.papel || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className={`btn-toggle ${usuario.ativo ? 'ativo' : 'inativo'}`}
                          onClick={() => handleToggleAtivo(usuario.cpf)}
                        >
                          {usuario.ativo ? 'Ativo' : 'Inativo'}
                        </button>
                      </td>
                      <td className="acoes">
                        <button 
                          className="btn-editar" 
                          onClick={() => handleEditar(usuario)}
                          title="Editar"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="btn-deletar" 
                          onClick={() => handleDeletar(usuario.cpf, usuario.nome)}
                          title="Deletar"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {usuariosFiltrados.length === 0 && !loading && (
                <p className="sem-resultados">Nenhum usuário encontrado</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal Senha Master */}
      <ModalSenhaMaster 
        isOpen={modalSenhaMasterAberto}
        onClose={() => setModalSenhaMasterAberto(false)}
        onSenhaMasterCorreta={handleSenhaMasterCorreta}
      />
    </div>
  );
}

export default ModalPermissoes;
